import styles from './MultiSelectDropdown.module.scss'
import React, { useEffect, useRef, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { UseFormRegister } from 'react-hook-form'
import { useOutsideClick } from '../../common/useOutsideClick'

export interface MultiSelectOption {
  id: string
  label: string
  disabled?: boolean
}

export interface MultiSelectDropdownProps {
  register?: UseFormRegister<any>
  name: string
  options: MultiSelectOption[]
  onSelectedChanged?: (ids: string[]) => void
  initialSelectedOptions?: string[]
  placeholder?: string
  isInvalid?: boolean
  disabled?: boolean
  className?: string
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  register,
  name,
  options,
  onSelectedChanged,
  initialSelectedOptions = [],
  placeholder = 'Select Options',
  isInvalid = false,
  disabled = false,
  className = '',
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialSelectedOptions)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setSelectedOptions(initialSelectedOptions)
  }, [initialSelectedOptions])

  useEffect(() => {
    if (!isOpen && JSON.stringify(selectedOptions) !== JSON.stringify(initialSelectedOptions)) {
      onSelectedChanged && onSelectedChanged(selectedOptions)
    }
  }, [selectedOptions, isOpen])

  const toggleDropdown = () => {
    !disabled && setIsOpen(!isOpen)
  }

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optionId = event.target.value
    const isChecked = event.target.checked

    if (isChecked) {
      setSelectedOptions([...selectedOptions, optionId])
    } else {
      setSelectedOptions(selectedOptions.filter((selectedOptionId) => selectedOptionId !== optionId))
    }
  }

  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    setIsOpen(false)
  })
  const { onChange = undefined, onBlur = undefined, name: regName = undefined, ref = undefined } = register ? { ...register(name) } : {}
  return (
    <Container ref={containerRef} className={className}>
      <Row>
        <Col className='p-0 w-100 position-relative'>
          <div
            className={classNames(`${styles.multiSelectContainer}`, {
              [styles.multiSelectContainerOpen]: isOpen,
              [styles.multiSelectContainerInvalid]: isInvalid,
              [styles.multiSelectContainerDisabled]: disabled,
            })}
            role='button'
            id='multiSelectDropdown'
            onClick={toggleDropdown}
          >
            {selectedOptions.length ? (
              <div className='d-flex gap-1 flex-wrap'>
                <div className='paragraph-medium'>
                  {selectedOptions.length} {`option${selectedOptions.length > 1 ? 's' : ''} selected`}
                </div>
              </div>
            ) : (
              <div className='paragraph-medium zocks-text-gray-500'>{placeholder}</div>
            )}
            {isOpen ? <i className='bi bi-chevron-contract'></i> : <i className='bi bi-chevron-expand'></i>}
          </div>

          <div className={`overflow-auto dropdown-menu ${isOpen ? 'show' : ''} ${styles.multiSelectDropdownMenu} w-100`} aria-labelledby='multiSelectDropdown'>
            {options.map((option) => (
              <fieldset key={option.id} disabled={option.disabled}>
                <Form.Check
                  name={regName || name}
                  type='checkbox'
                  id={`${regName || name}_option_${option.id}`}
                  label={option.label}
                  checked={selectedOptions.includes(option.id)}
                  onChange={onChange || handleOptionChange}
                  value={option.id}
                  onBlur={onBlur}
                  ref={ref}
                />
              </fieldset>
            ))}
          </div>
        </Col>
      </Row>
    </Container>
  )
}
